import { Icon } from "@iconify/react";

import { BotaoPrevia } from "../components/PreviaLink";
import { MenuDeSetores, useSetores } from "./MenuDeSetores";
import { useEffect, useMemo, useState } from "react";

import {
  Botao,
  Campo,
  Carregando,
  Tag,
  Vazio,
} from "../components/Sga";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  hhmm,
  kindDoNivel,
  segundos,
  telefone,
} from "../lib/format";
import type {
  Conexao,
  LinkFormulario,
  LogIntegracao,
  PerfilAntiban,
} from "../lib/types";

interface Detalhe {
  conexao: Conexao;
  perfil_antiban: PerfilAntiban;
  ultimos_eventos: LogIntegracao[];
  sparkline: number[];
  webhook_entrada: string;
}

/** Campos que o operador pode mudar nesta tela. */
interface Edicao {
  nome: string;
  setor: string;
  observacoes: string;
  perfilKind: PerfilAntiban["kind"];
  desk: boolean;
  gestor: boolean;
  petro: boolean;
  /** Recebe mensagem de grupo de WhatsApp (so Evolution). */
  grupos: boolean;
}

export function ConexaoCadastro({
  conexaoId,
  perfis,
  aoVoltar,
  aoSalvo,
  aoReemparelhar,
  aoVerLogs,
  podeEscrever,
}: {
  conexaoId: string;
  perfis: PerfilAntiban[];
  aoVoltar: () => void;
  aoSalvo: (nome: string) => void;
  aoReemparelhar: (id: string) => void;
  aoVerLogs: () => void;
  podeEscrever: boolean;
}) {

  const { tenantAtivo } = useAuth();

  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  /** Link da fila desta conexão. Só existe no canal de formulário. */
  const [linkFila, setLinkFila] = useState<LinkFormulario | null>(null);
  const [copiadoFila, setCopiadoFila] = useState(false);

  // O link da fila pertence à conexão, não ao cliente: é a porta de entrada
  // daquela fila de atendimento. O link por CNPJ continua em Clientes e links.
  useEffect(() => {
    api
      .get<{ filas: { conexao_id: string; link: LinkFormulario | null }[] }>(
        "/links-fila",
      )
      .then((d) =>
        setLinkFila(
          d.filas.find((f) => f.conexao_id === conexaoId)?.link ?? null,
        ),
      )
      .catch(() => setLinkFila(null));
  }, [conexaoId]);

  const gerarLinkFila = async (rotacionar: boolean) => {
    if (rotacionar) {
      const ok = window.confirm(
        "Gerar um token novo para esta fila?\n\n" +
          "O link atual para de funcionar na hora — inclusive o que já estiver publicado no seu " +
          "site, na assinatura de e-mail e nas notas fiscais já enviadas.",
      );
      if (!ok) return;
    }
    const d = await api.post<{ link: LinkFormulario }>("/links-fila", {
      conexao_id: conexaoId,
      rotacionar,
    });
    setLinkFila(d.link);
  };

  const copiarLinkFila = async () => {
    if (!linkFila) return;
    try {
      await navigator.clipboard.writeText(linkFila.url);
      setCopiadoFila(true);
      window.setTimeout(() => setCopiadoFila(false), 1800);
    } catch {
      window.prompt("Copie o link:", linkFila.url);
    }
  };

  /** Valor original, para saber o que mudou de fato. */
  const [original, setOriginal] = useState<Edicao | null>(null);
  const [ed, setEd] = useState<Edicao | null>(null);
  const setores = useSetores();
  /* Abas em vez de um rolo unico.
   *
   * A tela juntava identidade, provedor, anti-ban, integracoes, link de
   * suporte, menu de setores, horario e webhook — tudo aberto ao mesmo tempo,
   * numa coluna so. Quem entrava para trocar o setor rolava por credencial de
   * provedor e teto de envio no caminho, e nada indicava o que ainda vinha
   * abaixo.
   *
   * Nenhum recurso saiu: os mesmos blocos, agrupados por **tarefa**. Quem vem
   * configurar atendimento nao esbarra em entrega, e quem vem mexer em entrega
   * nao rola por atendimento. */
  const [aba, setAba] = useState<"geral" | "atendimento" | "entrega">("geral");

  useEffect(() => {
    let cancelado = false;
    setDetalhe(null);
    setErro(null);

    api
      .get<Detalhe>(`/conexoes/${conexaoId}`)
      .then((d) => {
        if (cancelado) return;
        const inicial: Edicao = {
          nome: d.conexao.nome,
          setor: d.conexao.setor ?? "",
          observacoes: d.conexao.observacoes ?? "",
          perfilKind: d.perfil_antiban.kind,
          desk: d.conexao.integra_desk,
          gestor: d.conexao.integra_gestor,
          petro: d.conexao.integra_petro,
          grupos: d.conexao.aceita_grupos ?? false,
        };
        setDetalhe(d);
        setOriginal(inicial);
        setEd(inicial);
      })
      .catch((e) => !cancelado && setErro(e.message));

    return () => {
      cancelado = true;
    };
  }, [conexaoId]);

  const sujo = useMemo(
    () =>
      Boolean(ed && original) &&
      JSON.stringify(ed) !== JSON.stringify(original),
    [ed, original],
  );

  if (erro && !detalhe) {
    return (
      <div className="card-form">
        <div className="sga-vazio sga-vazio--erro">
          <Icon icon="mdi:alert-circle-outline" width={22} />
          {erro}
        </div>
      </div>
    );
  }
  if (!detalhe || !ed) return <Carregando texto="Abrindo cadastro…" />;

  const c = detalhe.conexao;
  const perfil =
    perfis.find((p) => p.kind === ed.perfilKind) ?? detalhe.perfil_antiban;
  const semAntiban = !c.usa_qr;
  const mudar = <K extends keyof Edicao>(campo: K, valor: Edicao[K]) =>
    setEd((atual) => (atual ? { ...atual, [campo]: valor } : atual));

  // Salvar só faz sentido com nome e setor — os dois campos marcados
  // obrigatórios. Sem isso o asterisco seria decorativo.
  const faltaCampo = !ed.nome.trim()
    ? "Informe o nome da conexão"
    : !ed.setor
      ? "Selecione o setor responsável"
      : null;

  const salvar = async () => {
    if (faltaCampo) return;
    setSalvando(true);
    try {
      await api.patch(`/conexoes/${c.id}`, {
        nome: ed.nome.trim(),
        setor: ed.setor || null,
        observacoes: ed.observacoes,
        perfil_antiban: ed.perfilKind,
        integra_desk: ed.desk,
        integra_gestor: ed.gestor,
        integra_petro: ed.petro,
        aceita_grupos: ed.grupos,
      });
      setOriginal(ed);
      aoSalvo(ed.nome.trim());
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  const voltar = () => {
    if (sujo && !window.confirm("Há alterações não salvas. Sair e descartar?"))
      return;
    aoVoltar();
  };

  const encerrarSessao = async () => {
    if (
      !window.confirm(
        `Encerrar a sessão de "${c.nome}"? Ela para de enviar até reemparelhar.`,
      )
    ) {
      return;
    }
    try {
      await api.del(`/conexoes/${c.id}/sessao`);
      aoSalvo(`Sessão de "${c.nome}" encerrada`);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  const copiarWebhook = async () => {
    try {
      await navigator.clipboard.writeText(detalhe.webhook_entrada);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    } catch {
      setErro("O navegador bloqueou o acesso à área de transferência.");
    }
  };

  // O rótulo da identidade muda por canal, como no handoff.
  const rotuloIdentidade =
    c.canal === "cloud"
      ? "Conta comercial"
      : c.canal === "telegram"
        ? "Bot"
        : "Dispositivo";

  return (
    <div className="card-form">
      <div className="card-form-head">
        <Icon icon="ant-design:api-outlined" width={30} className="ttl-icon" />
        <div>
          <div className="ttl">{c.nome}</div>
          <div className="ttl-sub">
            Cadastro de conexão · {c.canal_label} · {c.codigo}
          </div>
        </div>

        <div className="card-form-head-actions">
          {sujo && (
            <span className="cad-sujo">
              <Icon icon="mdi:alert-circle-outline" width={14} />
              Alterações não salvas
            </span>
          )}
          <Botao
            variante="primary"
            icone="mdi:content-save-outline"
            onClick={salvar}
            disabled={!podeEscrever || salvando || !sujo || Boolean(faltaCampo)}
            title={
              !podeEscrever
                ? "Seu perfil é somente leitura"
                : (faltaCampo ?? (sujo ? undefined : "Nada foi alterado"))
            }
          >
            {salvando ? "Salvando…" : "Salvar"}
          </Botao>
          <Botao icone="mdi:text-box-search-outline" onClick={aoVerLogs}>
            Log
          </Botao>
          <Botao variante="warning" icone="mdi:chevron-left" onClick={voltar}>
            Voltar
          </Botao>
        </div>
      </div>

      <div className="card-form-body">
        {erro && (
          <div
            className="sga-aviso-warn"
            style={{
              background: "rgba(255,77,79,.12)",
              color: "var(--sga-error)",
              marginBottom: 12,
            }}
          >
            <Icon icon="mdi:alert-circle-outline" width={17} />
            {erro}
          </div>
        )}

        <div
          // Coluna unica.
          //
          // A direita trazia Status, Trafego e Ultimos eventos, sempre abertos,
          // ao lado de um formulario que ja tinha assunto demais. Status e
          // trafego ja aparecem na lista de conexoes e no painel; aqui eram
          // repeticao ocupando metade da largura. Os eventos continuam, dentro
          // da aba de entrega, que e onde se vai quando algo nao chegou.
          className="sga-grid"
          style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
        >
          {/* ================================================= editáveis */}
          <div>
            <div className="cad-abas" role="tablist">
              {(
                [
                  ["geral", "Geral", "mdi:card-account-details-outline"],
                  ...(c.canal === "form"
                    ? [["atendimento", "Atendimento", "mdi:headset"] as const]
                    : []),
                  ["entrega", "Entrega e integrações", "mdi:swap-horizontal"],
                ] as const
              ).map(([id, rotulo, icone]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={aba === id}
                  className={"cad-aba" + (aba === id ? " on" : "")}
                  onClick={() => setAba(id)}
                >
                  <Icon icon={icone} width={15} />
                  {rotulo}
                </button>
              ))}
            </div>

            {aba === "geral" && (<>
            <div className="cad-secao">
              <Icon
                icon="mdi:card-account-details-outline"
                width={17}
                style={{ color: "var(--sga-primary-text)" }}
              />
              <span className="cad-secao__titulo">Identificação</span>
              <span className="cad-secao__linha" />
            </div>

            <div className="line line-2">
              <Campo
                rotulo="Nome da conexão"
                obrigatorio
                hint="Aparece nas filas, nos logs e no SGA Desk."
                flex={3}
              >
                <input
                  className="input"
                  value={ed.nome}
                  onChange={(e) => mudar("nome", e.target.value)}
                  maxLength={120}
                  disabled={!podeEscrever}
                  placeholder="Ex.: WhatsApp Cobrança"
                />
              </Campo>
              <Campo rotulo="Setor responsável" obrigatorio flex={2}>
                <select
                  className="select"
                  value={ed.setor}
                  onChange={(e) => mudar("setor", e.target.value)}
                  disabled={!podeEscrever}
                >
                  <option value="">Selecione…</option>
                  {/* O setor que ja esta gravado entra na lista mesmo se
                      tiver sido desativado depois: sem isso o campo abriria em
                      branco e salvar apagaria o setor sem ninguem pedir. */}
                  {(setores.some((x) => x.nome === ed.setor) || !ed.setor
                    ? setores.map((x) => x.nome)
                    : [ed.setor, ...setores.map((x) => x.nome)]
                  ).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Campo>
            </div>

            <div className="line line-1" style={{ maxWidth: "none" }}>
              <Campo
                rotulo="Observações"
                hint="Contexto para quem for operar esta conexão depois."
              >
                <textarea
                  className="textarea"
                  value={ed.observacoes}
                  onChange={(e) => mudar("observacoes", e.target.value)}
                  maxLength={2000}
                  disabled={!podeEscrever}
                  rows={2}
                  style={{ minHeight: 56 }}
                  placeholder="Opcional"
                />
              </Campo>
            </div>

            {/* ============================================ identidade */}
            <div className="cad-secao">
              <Icon
                icon="mdi:cellphone-check"
                width={17}
                style={{ color: "var(--sga-primary-text)" }}
              />
              <span className="cad-secao__titulo">Identidade autenticada</span>
              <span className="cad-secao__linha" />
              <span className="cad-secao__nota">vem do provedor</span>
            </div>

            <div className="cad-dados">
              <Dado
                rotulo="Canal"
                icone="mdi:vector-link"
                valor={c.canal_label}
              />
              <Dado
                rotulo="Número autenticado"
                icone="mdi:cellphone-check"
                valor={c.numero ? telefone(c.numero) : null}
                mono
              />
              <Dado
                rotulo={rotuloIdentidade}
                icone="mdi:devices"
                valor={c.device_label}
              />
              <Dado
                rotulo="Sessão desde"
                icone="mdi:clock-outline"
                valor={
                  c.sessao_desde
                    ? new Date(c.sessao_desde).toLocaleString("pt-BR")
                    : null
                }
              />
              <Dado
                rotulo="Código rápido"
                icone="mdi:pound"
                valor={c.codigo}
                mono
              />
              <Dado
                rotulo="Tenant"
                icone="mdi:office-building-outline"
                valor={tenantAtivo?.nome ?? null}
              />
            </div>

            <div className="cad-acoes-sessao">
              <Botao
                icone={c.usa_qr ? "mdi:qrcode-scan" : "mdi:check-decagram"}
                onClick={() => aoReemparelhar(c.id)}
                disabled={!podeEscrever}
              >
                {c.usa_qr
                  ? "Reemparelhar com QR Code"
                  : "Revalidar credenciais"}
              </Botao>
              <Botao
                icone="mdi:logout-variant"
                onClick={encerrarSessao}
                disabled={!podeEscrever || c.status === "offline"}
                title={
                  c.status === "offline"
                    ? "A sessão já está encerrada"
                    : undefined
                }
              >
                Encerrar sessão
              </Botao>
              <span className="hint" style={{ flex: 1, minWidth: 200 }}>
                {c.usa_qr
                  ? "Reemparelhar invalida a sessão atual e exige leitura do QR no aparelho."
                  : "A API oficial não usa QR: a revalidação confere WABA, número e token na Meta."}
              </span>
            </div>

            </>)}

            {aba === "entrega" && (<>
            {/* ============================================== anti-ban */}
            <div className="cad-secao">
              <Icon
                icon="mdi:shield-check-outline"
                width={17}
                style={{ color: "var(--sga-primary-text)" }}
              />
              <span className="cad-secao__titulo">Anti-Ban</span>
              <span className="cad-secao__linha" />
              <span className="cad-secao__nota">valores do perfil</span>
            </div>

            {semAntiban ? (
              <div className="sga-aviso-warn">
                <Icon icon="mdi:shield-off-outline" width={17} />
                {c.canal === "cloud"
                  ? "Canal oficial: a Meta cobra por conversa e limita por qualidade do número, não por cadência. O Anti-Ban não intervém aqui."
                  : "Este canal não sofre banimento por cadência — o limite é do próprio provedor."}
              </div>
            ) : (
              <>
                <div className="line line-2" style={{ marginBottom: 10 }}>
                  <Campo
                    rotulo="Perfil"
                    obrigatorio
                    hint="Trocar o perfil muda a cadência de envio desta conexão."
                    flex={2}
                  >
                    <select
                      className="select"
                      value={ed.perfilKind}
                      onChange={(e) =>
                        mudar(
                          "perfilKind",
                          e.target.value as PerfilAntiban["kind"],
                        )
                      }
                      disabled={!podeEscrever}
                    >
                      {perfis.map((p) => (
                        <option key={p.id} value={p.kind}>
                          {p.nome}
                        </option>
                      ))}
                    </select>
                  </Campo>
                  <div style={{ flex: 3 }} />
                </div>

                <div className="cad-pilulas">
                  <Pilula
                    rotulo="Intervalo entre envios"
                    valor={`${segundos(perfil.min_interval_ms)} – ${segundos(perfil.max_interval_ms)}`}
                  />
                  <Pilula
                    rotulo="Limite por hora"
                    valor={
                      perfil.hourly_limit
                        ? `${perfil.hourly_limit} msg/h`
                        : null
                    }
                    vazio="sem limite"
                  />
                  <Pilula
                    rotulo="Pausa a cada"
                    valor={
                      perfil.pause_every ? `${perfil.pause_every} envios` : null
                    }
                    vazio="sem pausa"
                  />
                  <Pilula
                    rotulo="Duração da pausa"
                    valor={
                      perfil.pause_duration_s
                        ? `${Math.round(perfil.pause_duration_s / 60)} min`
                        : null
                    }
                  />
                  <Pilula
                    rotulo="Fracionamento"
                    valor={`${perfil.batch_size} por janela`}
                  />
                  <Pilula
                    rotulo="Aquecimento"
                    valor={
                      perfil.warmup_days ? `${perfil.warmup_days} dias` : null
                    }
                    vazio="não aplica"
                  />
                </div>
                <p className="hint" style={{ marginTop: 8 }}>
                  Estes números vêm do perfil, não desta conexão. Para
                  alterá-los, edite o perfil em Filas e Anti-Ban — a mudança
                  vale para todas as conexões que o usam.
                </p>
              </>
            )}

            {/* =========================================== integrações */}
            <div className="cad-secao">
              <Icon
                icon="mdi:vector-link"
                width={17}
                style={{ color: "var(--sga-primary-text)" }}
              />
              <span className="cad-secao__titulo">Integrações</span>
              <span className="cad-secao__linha" />
              <span className="cad-secao__nota">quem recebe o inbound</span>
            </div>

            <div className="cad-integra">
              {(
                [
                  ["desk", "SGA Desk", "tickets de atendimento"],
                  ["gestor", "SGA Gestor", "campanhas, boletos, NF"],
                  ["petro", "SGA Petro", "retaguarda"],
                ] as const
              ).map(([chave, nome, sub]) => {
                const ligado = ed[chave];
                return (
                  <label
                    key={chave}
                    className={`cad-integra__item${ligado ? " cad-integra__item--on" : " cad-integra__item--off"}`}
                  >
                    <input
                      type="checkbox"
                      checked={ligado}
                      onChange={(e) => mudar(chave, e.target.checked)}
                      disabled={!podeEscrever}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span className="cad-integra__nome">{nome}</span>
                      <span
                        className="cad-integra__sub"
                        style={{ display: "block" }}
                      >
                        {sub}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            {/* ===================================== grupos de WhatsApp */}
            {/* So Evolution: e o unico canal em que o gateway sabe ler e responder
                grupo (`grupos.rs`). Desligado = grupo descartado, como sempre foi. */}
            {c.canal === "evolution" && (<>
              <div className="cad-secao">
                <Icon
                  icon="mdi:account-group"
                  width={17}
                  style={{ color: "var(--sga-primary-text)" }}
                />
                <span className="cad-secao__titulo">Grupos de WhatsApp</span>
                <span className="cad-secao__linha" />
                <span className="cad-secao__nota">desligado = grupo ignorado</span>
              </div>
              <div className="cad-integra">
                <label
                  className={`cad-integra__item${ed.grupos ? " cad-integra__item--on" : " cad-integra__item--off"}`}
                >
                  <input
                    type="checkbox"
                    checked={ed.grupos}
                    onChange={(e) => mudar("grupos", e.target.checked)}
                    disabled={!podeEscrever}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span className="cad-integra__nome">Receber grupos de WhatsApp</span>
                    <span
                      className="cad-integra__sub"
                      style={{ display: "block" }}
                    >
                      mensagens de grupo vão para a aba Grupos do SGA Desk, fora da fila
                    </span>
                  </span>
                </label>
              </div>
            </>)}

            </>)}

            {aba === "atendimento" && c.canal === "form" && (
              <div>
                <div className="field-label" style={{ marginBottom: 4 }}>
                  Link de suporte desta fila
                </div>
                {linkFila ? (
                  <>
                    <div className="cad-copia">
                      <code>{linkFila.url}</code>
                      <button type="button" onClick={copiarLinkFila}>
                        <Icon
                          icon={
                            copiadoFila ? "mdi:check-bold" : "mdi:content-copy"
                          }
                          width={14}
                        />
                        {copiadoFila ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 8,
                        alignItems: "center",
                      }}
                    >
                      <Tag>{linkFila.submissoes} atendimentos</Tag>
                      <span style={{ flex: 1 }} />
                      <BotaoPrevia url={linkFila.url} />
                      <Botao
                        icone="mdi:autorenew"
                        onClick={() => gerarLinkFila(true)}
                        disabled={!podeEscrever}
                        title="Gera um token novo. O link atual para de funcionar."
                      >
                        Rotacionar
                      </Botao>
                    </div>
                    <p className="hint" style={{ marginTop: 5 }}>
                      Publique no seu site, na assinatura de e-mail e na nota
                      fiscal. O cliente se identifica sozinho no primeiro acesso
                      — nome do posto, CNPJ, celular — e o navegador dele lembra
                      depois.
                    </p>
                  </>
                ) : (
                  <>
                    <Botao
                      variante="primary"
                      icone="mdi:link-plus"
                      onClick={() => gerarLinkFila(false)}
                      disabled={!podeEscrever}
                    >
                      Gerar link da fila
                    </Botao>
                    <p className="hint" style={{ marginTop: 5 }}>
                      Um link só para esta fila, que qualquer cliente pode usar.
                      Para link dedicado por CNPJ, use a tela{" "}
                      <strong>Clientes e links</strong>.
                    </p>
                  </>
                )}

                <MenuDeSetores
                  conexaoId={c.id}
                  setorDaConexao={c.setor ?? null}
                  podeEscrever={podeEscrever}
                />
              </div>
            )}

            {aba === "entrega" && (
            <div style={{ marginTop: 18 }}>
          <div className="sga-subcard">
            <h4 className="sga-subcard__titulo">
              <Icon icon="mdi:message-processing-outline" width={16} />{" "}
              Últimos eventos
            </h4>
            {detalhe.ultimos_eventos.length === 0 ? (
              <Vazio
                icone="mdi:check-circle-outline"
                texto="Nenhum evento registrado"
              />
            ) : (
              detalhe.ultimos_eventos.map((e) => (
                <div
                  key={e.id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "7px 0",
                    borderBottom: "1px solid var(--sga-border-gray)",
                    fontSize: 12.5,
                  }}
                >
                  <Icon
                    icon={
                      kindDoNivel(e.nivel) === "ok"
                        ? "mdi:check-circle-outline"
                        : "mdi:alert-circle-outline"
                    }
                    width={15}
                    style={{
                      color:
                        kindDoNivel(e.nivel) === "ok"
                          ? "var(--sga-tag-text)"
                          : kindDoNivel(e.nivel) === "warn"
                            ? "var(--sga-warning)"
                            : "var(--sga-error)",
                      flex: "none",
                      marginTop: 2,
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>{e.evento}</span>
                  <span
                    className="mono"
                    style={{ color: "var(--sga-fg-3)", fontSize: 11.5 }}
                  >
                    {hhmm(e.created_at)}
                  </span>
                </div>
              ))
            )}
            <Botao
              variante="link"
              onClick={aoVerLogs}
              style={{ marginTop: 8 }}
            >
              Ver todos os logs
            </Botao>
          </div>
            </div>
            )}

            {aba === "entrega" && (
            <div style={{ marginTop: 14 }}>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Webhook de entrada
              </div>
              <div className="cad-copia">
                <code>{detalhe.webhook_entrada}</code>
                <button type="button" onClick={copiarWebhook}>
                  <Icon
                    icon={copiado ? "mdi:check-bold" : "mdi:content-copy"}
                    width={14}
                  />
                  {copiado ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="hint" style={{ marginTop: 5 }}>
                É por esta URL que o container do canal entrega as mensagens no
                gateway.
              </p>
            </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

/** Dado só-leitura. Travessão quando não há valor — nunca campo vazio sem explicação. */
function Dado({
  rotulo,
  icone,
  valor,
  mono,
}: {
  rotulo: string;
  icone: string;
  valor: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="cad-dado">
      <div className="cad-dado__rotulo">
        <Icon icon={icone} width={12} />
        {rotulo}
      </div>
      <div
        className={`cad-dado__valor${mono && valor ? " cad-dado__valor--mono" : ""}${
          valor ? "" : " cad-dado__valor--vazio"
        }`}
        title={valor ?? undefined}
      >
        {valor || "—"}
      </div>
    </div>
  );
}

function Pilula({
  rotulo,
  valor,
  vazio,
}: {
  rotulo: string;
  valor: string | null;
  vazio?: string;
}) {
  return (
    <div className="cad-pilula">
      <div className="cad-pilula__rotulo">{rotulo}</div>
      <div
        className={`cad-pilula__valor${valor ? "" : " cad-pilula__valor--neutro"}`}
      >
        {valor ?? vazio ?? "—"}
      </div>
    </div>
  );
}


